import {nonEmpty, stringCompare} from '@lib/utils';
import {createSelector} from '@reduxjs/toolkit';
import groupBy from 'lodash/groupBy';
import {
  compareByMeetingResponse,
  getAttendeesBySelectedMeeting,
  GroupedMeetingAttendeesItem,
  MeetingAttendeeType,
  getLoggedInUserAsAttendee,
  getMeetingRoomsGroupedByBuilding,
  getUserDirectoryObjectId,
  getSelectedMeetingAttendeeGroupId,
  getDefaultBuildingId,
  MeetingAttendeeGroups,
  getLocationForDate,
} from '../../data';
import {RootState} from '../../rootReducer';
import {
  compareGroupsToPopDefaultBuildingToTop,
  compareGroupsToPopUserGroupToTop,
  mapAttendeesToAttendeeGroups,
} from './helpers';

const getAttendeeGroupsByLocationType = createSelector(
  (_: RootState, date: string) => date,
  getAttendeesBySelectedMeeting,
  (date, attendees): MeetingAttendeeGroups => {
    //Groups by location type
    const locationGroups = groupBy(attendees, (attendee) => getLocationForDate(attendee, date).locationType);

    // Groups local group by building
    const buildingGroups = groupBy(
      locationGroups[MeetingAttendeeType.Local],
      (attendee) => getLocationForDate(attendee, date)?.shiftBuildingId,
    );
    const localAttendees = Object.values(buildingGroups);

    return {
      localGroups: localAttendees ?? [],
      remoteGroup: locationGroups[MeetingAttendeeType.Remote] ?? [],
      unknownGroup: locationGroups[MeetingAttendeeType.Unknown] ?? [],
    };
  },
);

const getFullGroupsMappedToUI = createSelector(
  (state: RootState, _meetingId: string, date: string) => getAttendeeGroupsByLocationType(state, date),
  (_state: RootState, _meetingId: string, date: string) => date,
  getMeetingRoomsGroupedByBuilding,
  ({localGroups, remoteGroup, unknownGroup}, date, rooms) => {
    // Mapping local attendee groups to UI model
    const attendeeBasedGroups: GroupedMeetingAttendeesItem[] = localGroups.map((buildingGroup) => {
      const {shiftBuildingId = '', shiftBuildingName = ''} = getLocationForDate(buildingGroup[0], date);
      return {
        attendees: [...buildingGroup].sort(compareByMeetingResponse),
        id: shiftBuildingId,
        buildingId: shiftBuildingId,
        locationDescription: shiftBuildingName,
        locationType: MeetingAttendeeType.Local,
      };
    });

    // Adding in rooms that don't have attendees
    const buildingIdsForMeeting = Object.keys(rooms ?? {});
    const buildingsIdsWithoutAttendees = buildingIdsForMeeting?.filter(
      (buildingId) => !attendeeBasedGroups?.find((group) => group.buildingId === buildingId),
    );

    const roomOnlyGroups: GroupedMeetingAttendeesItem[] = buildingsIdsWithoutAttendees.map((buildingId) => ({
      attendees: [],
      id: buildingId,
      buildingId,
      locationDescription: rooms[buildingId]?.[0]?.buildingName,
      locationType: MeetingAttendeeType.Local,
    }));

    // Merging attendee and room groups
    const mergedLocalGroups = [...attendeeBasedGroups, ...roomOnlyGroups].sort((a, b) =>
      stringCompare(a.locationDescription, b.locationDescription),
    );

    const remoteGroupForUi = mapAttendeesToAttendeeGroups(remoteGroup, MeetingAttendeeType.Remote);
    const unknownGroupForUI = mapAttendeesToAttendeeGroups(unknownGroup, MeetingAttendeeType.Unknown);

    return nonEmpty([...mergedLocalGroups, remoteGroupForUi, unknownGroupForUI]);
  },
);

export const getFullGroupsMappedToUISorted = createSelector(
  getFullGroupsMappedToUI,
  getUserDirectoryObjectId,
  getDefaultBuildingId,
  (localGroups, userDirectoryObjectId, defaultBuildingId): GroupedMeetingAttendeesItem[] => {
    const groupsWithDefaultBuildingOnTop = [...localGroups].sort((a, b) =>
      compareGroupsToPopDefaultBuildingToTop(a, b, defaultBuildingId),
    );

    return [...groupsWithDefaultBuildingOnTop].sort((a, b) =>
      compareGroupsToPopUserGroupToTop(a, b, userDirectoryObjectId),
    );
  },
);

/** Get selected group for details screen */
export const getMeetingAttendeeGroupById = createSelector(
  getSelectedMeetingAttendeeGroupId,
  getFullGroupsMappedToUISorted,
  (groupId, groups) => groups.find((group) => group.id === groupId),
);

export const getIsGroupedAcceptedAttendeesMatchingUserLocation = createSelector(
  getLoggedInUserAsAttendee,
  (_state: RootState, groupedAttendees: GroupedMeetingAttendeesItem) => groupedAttendees,
  (_state: RootState, _groupedAttendees: GroupedMeetingAttendeesItem, date: string) => date,
  (loggedInUserAsAttendee, groupedAttendees, date) => {
    const {locationType, shiftBuildingId} = loggedInUserAsAttendee
      ? getLocationForDate(loggedInUserAsAttendee, date)
      : {locationType: MeetingAttendeeType.Unknown, shiftBuildingId: undefined};

    return (
      (locationType === MeetingAttendeeType.Remote && groupedAttendees.locationType === 'Remote') ||
      (locationType === MeetingAttendeeType.Local && shiftBuildingId === groupedAttendees.buildingId)
    );
  },
);
